import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import { formatStatusName, getStatusStyle, formatDate, formatPrice } from '../../utils/entityUtils';
import { isClientRole, isPrestataireRole } from '../../utils/roleUtils';
import Coordonnees from '../../composants/Coordonnees/Coordonnees';
// @ts-ignore
const logoCentralis = require('../../assets/logo/logo-centralis-gris.png');
// @ts-ignore
const bandeauCentralis = require('../../assets/logo/bandeau-centralis.png');

const styles = StyleSheet.create({
  page: {
    padding: 0,
    fontSize: 12,
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFDFC'
  },
  bandeau: {
    width: '100%'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    paddingBottom: 16
  },
  logo: {
    width: 100,
    marginRight: 16
  },
  userInfo: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'right'
  },
  dateInfo: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'right',
    marginTop: 4
  },
  titleSection: {
    backgroundColor: '#484848',
    padding: 16,
    marginHorizontal: 24,
    marginTop: 8,
    marginBottom: 16,
    borderRadius: 8
  },
  projectTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFDFC',
    marginBottom: 4
  },
  projectSubtitle: {
    fontSize: 14,
    color: '#FFFDFC'
  },
  infoSection: {
    padding: 16,
    marginHorizontal: 24,
    marginBottom: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB'
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 8
  },
  infoLabel: {
    fontWeight: 'bold',
    width: '40%',
    fontSize: 11
  },
  infoValue: {
    flex: 1,
    fontSize: 11
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    backgroundColor: 'rgba(215, 217, 217, 0.25)',
    padding: 12,
    marginHorizontal: 24,
    marginTop: 16,
    marginBottom: 8,
    borderRadius: 8
  },
  sectionContent: {
    marginHorizontal: 24,
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#FFFDFC',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB'
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 4
  },
  fieldValue: {
    fontSize: 11,
    marginBottom: 12,
    lineHeight: 1.4
  },
  table: {
    marginTop: 12
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 24,
    alignItems: 'center'
  },
  tableHeader: {
    backgroundColor: 'rgba(215, 217, 217, 0.25)',
    fontWeight: 'bold'
  },
  tableCell: {
    flex: 1,
    fontSize: 10,
    padding: 6
  },
  tableCellNarrow: {
    width: 60,
    fontSize: 10,
    padding: 6
  },
  tableCellWide: {
    flex: 2,
    fontSize: 10,
    padding: 6
  },
  tableCellRight: {
    flex: 1,
    fontSize: 10,
    padding: 6,
    textAlign: 'right'
  },
  totalSection: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 12,
    paddingTop: 8,
    paddingRight: 6,
    borderTopWidth: 2,
    borderTopColor: '#E5E7EB'
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    marginRight: 8
  },
  totalValue: {
    fontSize: 12,
    fontWeight: 'bold'
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 24,
    right: 24,
    fontSize: 10,
    color: '#6B7280',
    textAlign: 'center',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB'
  },
  statusBadge: {
    marginVertical: 4,
    padding: 6,
    width: 120,
    textAlign: 'center',
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 'bold'
  },
  sectionHeader: {
    backgroundColor: '#f3f4f6',
    padding: 8,
    marginBottom: 2,
    fontWeight: 'bold',
    fontSize: 12,
    borderRadius: 4
  },
  actionsCell: {
    width: 80,
    fontSize: 10,
    padding: 6,
    textAlign: 'center'
  },
  detailTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    marginTop: 10
  },
  totalArticles: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'right',
    marginTop: 10,
    marginRight: 10,

  },
  consultationInfo: {
    backgroundColor: '#fff7ed',
    borderColor: '#FED7AA',
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    fontSize: 10,
    marginHorizontal: 24,
  }
});




// Fonction pour calculer le nombre total d'articles
const countTotalArticles = (sections: any[]) => {
  if (!sections || !Array.isArray(sections)) return 0;
  return sections.reduce((count, section) => count + (section.articles ? section.articles.length : 0), 0);
};

// Composant principal PDF
const ConsultationPDF = ({ commande, user, presta }: { commande: any; user: any; presta: any; }) => {



  // CoordonneesPDF intégré directement dans ConsultationPDF
  const CoordonneesPDF = ({
    isAcheteur,
    isPresta,
    rang
  }: {
    isAcheteur: boolean;
    isPresta: boolean;
    rang: any
  }) => {
    // On utilise directement `commande` depuis le scope de ConsultationPDF
    const dataPrestataire = isPresta
      ? commande.operation
      : commande.commandes?.find((c: any) => c.rang === rang);
    const dataAcheteur = isAcheteur ? commande : commande?.operation;
    // Adresse Prestataire
    const prestaAdresse = commande?.contrat?.entite?.adresse
      ? `${commande.contrat.entite.adresse.numero_voie || 'bla'} ${commande.contrat.entite.adresse.type_voie || ''} ${commande.contrat.entite.adresse.nom_voie || ''}, ${commande.contrat.entite.adresse.code_postal || ''} ${commande.contrat.entite.adresse.ville || ''} ${commande.contrat.entite.adresse.arrondissement || ''}`
      : `${dataPrestataire.contrat.entite.adresse.numero_voie || 'bla'} ${dataPrestataire.contrat.entite.adresse.type_voie || ''} ${dataPrestataire.contrat.entite.adresse.nom_voie || ''}, ${dataPrestataire.contrat.entite.adresse.code_postal || ''} ${dataPrestataire.contrat.entite.adresse.ville || ''} ${dataPrestataire.contrat.entite.adresse.arrondissement || ''}`;
    console.log('commande', commande)
    console.log('prestaAdresse', prestaAdresse);

    // Adresse Acheteur
    const acheteurAdresse = commande?.operation?.organisme?.adresse
      ? `${commande?.operation?.organisme?.adresse?.numero_voie || ''} ${commande?.operation?.organisme?.adresse?.nom_voie || ''}, ${commande?.operation?.organisme?.adresse?.code_postal || ''} ${commande?.operation?.organisme?.adresse?.ville || ''} ${commande?.operation?.organisme?.adresse?.arrondissement || ''} `
      : `${dataAcheteur.organisme?.adresse?.numero_voie || ''} ${dataAcheteur.organisme?.adresse?.nom_voie || ''}, ${dataAcheteur.organisme?.adresse?.code_postal || ''} ${dataAcheteur.organisme?.adresse?.ville || ''} ${dataAcheteur.organisme?.adresse?.arrondissement || ''} `;

    return (
      <View style={{ flexDirection: 'row', marginTop: 16, marginHorizontal: 24, justifyContent: 'space-between' }}>
        {/* Colonne Prestataire */}
        <View style={{ flex: 1, marginRight: 8 }}>
          <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>{commande?.contrat?.entite?.nom || dataPrestataire?.contrat?.entite?.nom || '-'}</Text>
          <Text>{prestaAdresse}</Text>
          <Text>Contrat N°{commande?.contrat?.numero || dataPrestataire?.contrat?.numero || '-'}</Text>
          <View style={{ marginVertical: 8 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 2 }}>Prestataire : {commande?.contrat?.entite?.interlocuteur?.prenom || dataPrestataire?.contrat?.entite?.interlocuteur?.prenom} {commande?.contrat?.entite?.interlocuteur?.nom || dataPrestataire?.contrat?.entite?.interlocuteur?.nom}</Text>
            <Text style={{ marginBottom: 2 }}>{commande?.contrat?.entite?.interlocuteur?.fonction || dataPrestataire?.contrat?.entite?.interlocuteur?.fonction || "Chef de service"}</Text>
            <Text style={{ marginBottom: 2 }}>{commande?.contrat?.entite?.interlocuteur?.email_contact || dataPrestataire?.contrat?.entite?.interlocuteur?.email_contact || "sophie.martin@eiffage.fr"}</Text>
            <Text style={{ marginBottom: 2 }}>{commande?.contrat?.entite?.interlocuteur?.telephone || dataPrestataire?.contrat?.entite?.interlocuteur?.telephone || "06 78 98 09 98"}</Text>
          </View>
        </View>

        {/* Colonne Acheteur */}
        <View style={{ flex: 1, marginLeft: 8 }}>
          <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>{commande?.operation?.organisme?.nom || dataAcheteur?.organisme?.nom || '-'}</Text>
          <Text>{acheteurAdresse}</Text>
          <View style={{ marginVertical: 8 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 2 }}>Acheteur : {dataAcheteur.acheteur?.prenom || dataAcheteur?.organisme?.acheteur?.prenom} {dataAcheteur.acheteur?.nom || dataAcheteur?.organisme?.acheteur?.nom}</Text>
            <Text style={{ marginBottom: 2 }}>{dataAcheteur?.acheteur?.fonction || dataAcheteur?.organisme?.acheteur?.fonction || "Chef de service"}</Text>
            <Text style={{ marginBottom: 2 }}>{dataAcheteur.acheteur?.email_contact || dataAcheteur?.organisme?.acheteur?.email_contact || "sophie.martin@eiffage.fr"}</Text>
            <Text style={{ marginBottom: 2 }}>{dataAcheteur.acheteur?.telephone || dataAcheteur?.organisme?.acheteur?.telephone || "06 78 98 09 98"}</Text>
          </View>
          <Text style={{ marginTop: 4, fontWeight: 'bold' }}>Facturation suivie par : {dataAcheteur?.operation?.facturation || dataAcheteur?.facturation || '-'}</Text>
        </View>
      </View>
    );
  };

  const isPrestataire = isPrestataireRole(user?.role || '');
  const commandeEffective = isPrestataire ? commande.operation : commande;
  const isClientView = isClientRole(user?.role || '');

  const form = commandeEffective.formulaire || {};

  const today = formatDate(new Date(), true);

  const numero = commandeEffective.numero?.trim() || commandeEffective.operation?.numero?.trim() || '';
  let suffix = '';
  let typeCommande = '';

  if (numero?.toUpperCase().startsWith('PBC') || numero?.toUpperCase().startsWith('BC')) {
    suffix = 'BC';
    typeCommande = 'BC';
  } else if (numero?.toUpperCase().startsWith('PMS') || numero?.toUpperCase().startsWith('MS')) {
    suffix = 'MS';
    typeCommande = 'MS';
  }
  // Utiliser les sections à partir de form.sections d'abord, sinon utiliser commande.sections
  // const sections = form.sections || commandeEffective.sections || [];
  // console.log('section', sections)

  // Extraire les articles de toutes les sections
  // Extraire les articles de toutes les sections
  const articles: any[] = [];

  // Déterminer la source des articles
  let sourceCommande;

  if (isPrestataire) {
    // Prestataire : on prend la commande principale
    sourceCommande = commande;
  } else {
    // Acheteur : si plusieurs sous-commandes, on prend celle correspondant au rang
    if (Array.isArray(commande.commandes) && commande.commandes.length > 0) {
      const sousCommande = presta
        ? commande.commandes.find((c: any) => c.rang === presta)
        : commande.commandes[0]; // si presta non défini, on prend la première sous-commande
      sourceCommande = sousCommande || commande;
    } else {
      sourceCommande = commande;
    }
  }

  // DEBUG : vérifier ce qu'on va utiliser pour extraire les sections/articles
  console.log('sourceCommande pour extraction des articles:', sourceCommande);

  // Récupérer les sections
  const sections = sourceCommande.formulaire?.sections || sourceCommande.sections || [];
  // Extraire les articles
  if (Array.isArray(sections) && sections.length > 0) {
    sections.forEach(section => {
      if (Array.isArray(section.articles)) {
        articles.push(...section.articles);
      }
    });
  }

  // Si pas de sections mais articles directs
  if (
    (!Array.isArray(sections) || sections.length === 0) &&
    (Array.isArray(sourceCommande.articles) || Array.isArray(commandeEffective.articles))
  ) {
    const sourceArticles = Array.isArray(sourceCommande.articles)
      ? sourceCommande.articles
      : commandeEffective.articles;

    articles.push(...sourceArticles);
  }

  console.log('articles:', articles);






  // Si form.sections ou commande.sections existe -> MS
  // Sinon si commande.articles existe -> BC
  let sectionsToRender = [];

  if (sections && sections.length > 0) {
    sectionsToRender = sections;
  } else if (articles && articles.length > 0) {
    // Transformer les articles en "section" pour PDF
    sectionsToRender = [
      {
        id: 'default',
        name: 'Articles',
        articles: articles.map((a: any, index: number) => {
          const isPrestaFormat = !!a.catalogue; // s’il a un catalogue, c’est le format prestataire

          return {
            code: isPrestaFormat ? a.catalogue?.code : a.code || index + 1,
            nom: isPrestaFormat ? a.catalogue?.nom : a.nom,
            unite: isPrestaFormat ? a.catalogue?.unite : a.unite,
            quantity: isPrestaFormat
              ? a.catalogue?.article?.quantity
              : a.quantite ?? a.quantity ?? 0,
            prix_unitaire: isPrestaFormat
              ? a.catalogue?.article?.prix_unitaire
              : a.prix ?? a.prix_unitaire ?? 0,
            prix_total: isPrestaFormat
              ? a.catalogue?.article?.prix_total
              : a.totalHT ?? a.prix_total ?? 0,
          };
        }),
      },
    ];
  }

  const totalArticles = countTotalArticles(sectionsToRender);

  const formatedStatus = formatStatusName(commandeEffective?.status?.nom || commande?.status?.nom);
  console.log('formated status:', formatedStatus)
  // S'assurer que status.nom existe avant de l'utiliser
  const statusStyle = getStatusStyle(formatedStatus || commandeEffective?.status?.nom || '');
  console.log('statusStyles:', statusStyle, commandeEffective.status?.nom.toLowerCase())
  console.log('articles:', articles);

  const totalPrix = sectionsToRender.reduce((sum, section) => {
    if (!section.articles) return sum;
    return sum + section.articles.reduce((s, a) => s + (a.prix_total ?? 0), 0);
  }, 0);


  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Bandeau Centralis */}
        {/* <Image src={bandeauCentralis} style={styles.bandeau} /> */}

        {/* En-tête avec informations utilisateur et date */}
        <View style={styles.header} wrap={false}>
          <View>
            <Text style={styles.userInfo}>{user?.email || 'Utilisateur'}</Text>
            <Text style={styles.dateInfo}>Document généré le {today}</Text>
          </View>
        </View>

        {/* Informations générales */}
        <View style={styles.infoSection} wrap={false}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Consultation</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>
              N° {numero || `${suffix}${commandeEffective.id} `} • {suffix}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Lot technique:</Text>
            <Text style={styles.infoValue}>{commandeEffective.lot_technique?.nom || '-'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Département:</Text>
            <Text style={styles.infoValue}>{commandeEffective.lot_departement?.departement?.id} - {commandeEffective.lot_departement?.departement?.nom || '-'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Organisme:</Text>
            <Text style={styles.infoValue}>{commandeEffective.organisme?.nom || '-'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Date de création:</Text>
            <Text style={styles.infoValue}>{formatDate(commandeEffective?.created_at, true)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Statut:</Text>
            <View style={[styles.statusBadge, { backgroundColor: statusStyle.backgroundColor }]}>
              <Text style={{ color: statusStyle.color }}>{formatedStatus || 'Brouillon'}</Text>
            </View>
          </View>
        </View>

        {/* Section Descriptif Administratif */}
        <Text style={styles.sectionTitle}>Descriptif Administratif</Text>
        <View style={styles.sectionContent} wrap={false}>
          <View>
            <Text style={styles.fieldLabel}>Nom de l'opération</Text>
            <Text style={styles.fieldValue}>{form.nomOperation || commande.nom || '-'}</Text>
          </View>

          <View>
            <Text style={styles.fieldLabel}>Référence interne</Text>
            <Text style={styles.fieldValue}>{form.reference || commandeEffective.reference || '-'}</Text>
          </View>

          <View>
            <Text style={styles.fieldLabel}>Adresse</Text>
            <Text style={styles.fieldValue}>
              {form.adresse || commandeEffective.adresse ? `${form.adresse || commandeEffective.adresse}, ${form.codePostal || commandeEffective.code_postal || ''} ${form.ville || commandeEffective.ville || ''} ` : '-'}
            </Text>
          </View>
          <View>
            <Text style={styles.fieldLabel}>Facturation suivie par</Text>

            <Text style={styles.fieldValue}>
              {form.facturation || commandeEffective.facturation || form.coordonnees_maitrise_ouvrage || commandeEffective.coordonnees_maitrise_ouvrage || '-'}
            </Text>
          </View>
          <View>
            <Text style={styles.fieldLabel}>Coordonnées maîtrise</Text>
            <Text style={styles.fieldValue}>
              {!form?.facturation || !commandeEffective?.facturation
                ? (form?.coordonnes_maitrise || commandeEffective?.coordonnees_maitrise || '-')
                : (form?.coordonnees_maitrise_ouvrage || commandeEffective?.coordonnees_maitrise_ouvrage || '-')
              }
            </Text>
          </View>


          {(commande.visite) && (
            <View>
              <Text style={styles.fieldLabel}>Conditions de visite</Text>
              <Text style={styles.fieldValue}>{form.conditionVisite || commandeEffective.conditionVisite || '-'}</Text>
            </View>
          )}
          <View>
            <Text style={styles.fieldLabel}>Dates</Text>
            <Text style={styles.fieldValue}>
              {`Date de début de consultation: ${formatDate(form.dateDebutConsultation, false) || formatDate(commandeEffective.delais, false) || '-'}`}
            </Text>
            <Text style={styles.fieldValue}>
              {`Délai de consultation: ${form.delaisConsultation || commandeEffective.consultation || commandeEffective.delais || '-'} jours`}
            </Text>
            <Text style={styles.fieldValue}>
              {`Date de fin de consultation: ${formatDate(form.dateFinConsultation, false) || formatDate(commandeEffective.delais, false) || '-'}`}
            </Text>
            <Text style={styles.fieldValue}>
              {`Date de début de l'opération: ${formatDate(form.dateDebutOperation, false) || formatDate(commandeEffective.date_debut_operation, false) || '-'}`}
            </Text>
            <Text style={styles.fieldValue}>
              {`Délai fin d'opération: ${form.delaisFinOperation || ' - '} jours`}
            </Text >
            <Text style={styles.fieldValue}>
              {`Date de fin de l'opération: ${formatDate(form.dateFinOperation, false) || formatDate(commandeEffective.date_fin_operation, false) || '-'}`}
            </Text>
          </View >
        </View >

        {/* Section Descriptif Technique - Groupée dans un View pour éviter la coupure */}
        < View wrap={false} break>
          <Text style={styles.sectionTitle}>Descriptif Technique</Text>
          <View style={styles.sectionContent}>
            <View>
              <Text style={styles.fieldLabel}>Cahier des charges <span className="desc-precision">(Les liens URL vers des pièces graphiques ou des documents complémentaires peuvent être proposés par l’acheteur à titre d’information, mais seules les informations écrites ci-dessous sont contractuelles. Par ailleurs, toutes précisions administratives ou juridiques de nature à contrevenir à l’accord-cadre général sans dérogations prévues au contrat est a considérer comme inexistantes dans ce descriptif)</span></Text>
              <Text style={styles.fieldValue}>{form.besoin || commandeEffective.besoin || '-'}</Text>
            </View>
          </View>
        </View >

        {/* Section Descriptif Financier - Ajout d'un saut de page */}

        < View wrap={false} break>
          <Text style={styles.sectionTitle}>Descriptif Financier</Text>
          <View style={styles.sectionContent}>
            <Text style={styles.detailTitle}>Détail du Prix Global et Forfaitaire (DPGF):</Text>

            {sectionsToRender.map((section, sectionIndex) => (
              <View key={`section-${sectionIndex}`} wrap={sectionIndex === 0} break={sectionIndex !== 0}>
                {/* Titre de la section */}
                <View style={styles.sectionHeader}>
                  <Text>{section.name || section.nom || `Section ${sectionIndex + 1}`}</Text>
                </View>

                {/* Entêtes du tableau */}
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={styles.tableCellNarrow}>N° article</Text>
                  <Text style={styles.tableCell}>Nom de l'article</Text>
                  <Text style={styles.tableCellNarrow}>Unité</Text>
                  {typeCommande === 'BC' && (
                    <>
                      <Text style={styles.tableCellNarrow}>Qté</Text>
                      <Text style={styles.tableCellNarrow}>Prix unitaire</Text>
                      <Text style={styles.tableCellNarrow}>Prix total</Text>
                    </>
                  )}
                </View>

                {/* Articles de la section */}
                {section.articles && section.articles.length > 0 ? (
                  // Pagination : diviser les articles par groupes de 10
                  Array(Math.ceil(section.articles.length / 10)).fill(0).map((_, groupIndex) => (
                    <View key={`group-${section.id}-${groupIndex}`} wrap={false} break={groupIndex !== 0}>
                      {section.articles.slice(groupIndex * 10, (groupIndex + 1) * 10).map((article: any, index: number) => (
                        <View key={`article-${section.id}-${groupIndex * 10 + index}`} style={styles.tableRow}>
                          <Text style={styles.tableCellNarrow}>{article.code || (groupIndex * 10 + index + 1)}</Text>
                          <Text style={styles.tableCell}>{article.nom || '-'}</Text>
                          <Text style={styles.tableCellNarrow}>{article.unite || '-'}</Text>

                          {typeCommande === 'BC' && (
                            <>
                              <Text style={styles.tableCellNarrow}>
                                {article.quantity !== undefined ? article.quantity || article.quantite : '-'}
                              </Text>
                              <Text style={styles.tableCellNarrow}>
                                {typeof article.prix_unitaire === 'number' ? formatPrice(article.prix_unitaire) || formatPrice(article.prix) : '-'}
                              </Text>
                              <Text style={styles.tableCellNarrow}>
                                {typeof article.prix_total === 'number' ? formatPrice(article.prix_total) || formatPrice(article.totlaHT) : '-'}
                              </Text>
                            </>
                          )}
                        </View>
                      ))}
                    </View>
                  ))
                ) : (
                  <View style={styles.tableRow}>
                    <Text style={{ ...styles.tableCell, textAlign: 'center' }}>Aucun article</Text>
                  </View>
                )}
              </View>
            ))}
            {
              typeCommande === 'BC' && (
                <Text style={styles.totalArticles}>Total : {formatPrice(totalPrix)}</Text>
              )}
          </View>
        </View >

        {
          commandeEffective.formatedConsultationLancee && commandeEffective.consultationOperateur && (
            <View wrap={false}>
              <Text style={styles.consultationInfo}>Consultation lancée le {commandeEffective.formatedConsultationLancee} par {commandeEffective.consultationOperateur}</Text>
            </View>
          )
        }

        {/* Pied de page */}
        <Text style={styles.footer} fixed render={({ pageNumber, totalPages }) => (
          `Document généré par Centralis • ${today} • ${user?.email || ''} • Page ${pageNumber}/${totalPages}`
        )} />
      </Page >
    </Document >
  );
};

export default ConsultationPDF;